(() => {
  'use strict';

  const rootSelector = '[data-native-collection-pagination]';
  const gridSelector = '[data-native-product-grid]';
  const moreSelector = '[data-native-collection-more]';
  const statusSelector = '[data-native-collection-status]';
  const feedSelector = '[data-native-collection-products-feed]';
  const requestTimeoutMs = 10000;

  function resolveCollectionUrl(value) {
    if (!value) return null;

    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return null;
      url.hash = '';
      url.searchParams.delete('section_id');
      return url;
    } catch (_error) {
      return null;
    }
  }

  function pageNumber(url) {
    const page = Number.parseInt(url.searchParams.get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function hrefForFetch(url, page) {
    const requestUrl = new URL(url.href);
    if (page != null) requestUrl.searchParams.set('page', String(page));
    requestUrl.searchParams.set('section_id', 'native-collection-products');
    return `${requestUrl.pathname}${requestUrl.search}`;
  }

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  }

  function firstProductLink(cell) {
    return cell && cell.querySelector('a[href]');
  }

  function setLoading(root, link, status, loading) {
    root.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) {
      status.removeAttribute('data-native-pagination-error');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('aria-busy', 'true');
      status.textContent = 'Loading more products…';
      return;
    }

    root.removeAttribute('aria-busy');
    link.removeAttribute('aria-disabled');
    link.removeAttribute('aria-busy');
  }

  function initialize(root) {
    if (root.dataset.nativePaginationInitialized === 'true') return;

    const link = root.querySelector(moreSelector);
    const status = root.querySelector(statusSelector);
    const grid = document.querySelector(gridSelector);
    const firstCell = grid && Array.from(grid.children).find((child) => child.hasAttribute('data-native-product-id'));
    const initialNext = link && resolveCollectionUrl(link.getAttribute('href'));

    if (!link || !status || !grid || !initialNext || !firstCell) return;

    root.dataset.nativePaginationInitialized = 'true';
    const initialPage = Number.parseInt(root.dataset.nativeInitialPage || '', 10);
    const state = {
      batchSize: /^\d+$/.test(root.dataset.nativeBatchSize || '') && Number(root.dataset.nativeBatchSize) > 0 && Number(root.dataset.nativeBatchSize) % 4 === 0
        ? Number(root.dataset.nativeBatchSize) : null,
      loading: false,
      nextUrl: initialNext,
      needsBasePage: initialPage === 1,
      keyboardActivation: false,
      pendingCells: [],
      pendingPageUrl: null,
      autoPaused: false,
      suspended: false,
    };
    let observer = null;

    function watchForMore() {
      if (!observer) return;
      observer.disconnect();
      if (!state.loading && !state.autoPaused && !state.suspended && !document.hidden && root.isConnected && link.isConnected && (state.nextUrl || state.pendingCells.length)) {
        observer.observe(link);
      }
    }

    root.addEventListener('keydown', (event) => {
      if (
        event.target === link &&
        (event.key === 'Enter' || event.key === ' ') &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
      ) {
        state.keyboardActivation = true;
      }
    });

    root.addEventListener('pointerdown', (event) => {
      if (event.target === link) state.keyboardActivation = false;
    });

    root.addEventListener('click', (event) => {
      if (event.target !== link) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (typeof event.button === 'number' && event.button !== 0)) {
        state.keyboardActivation = false;
        return;
      }
      if (typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') return;

      event.preventDefault();
      const keyboardActivation = state.keyboardActivation || event.detail === 0;
      state.keyboardActivation = false;
      void loadProducts(keyboardActivation);
    });

    async function loadProducts(keyboardActivation = false) {
      if (state.loading || !root.isConnected || !grid.isConnected) return;

      state.loading = true;
      setLoading(root, link, status, true);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), requestTimeoutMs);
      let appendedCells = [];

      try {
        const seenIds = new Set(
          Array.from(grid.querySelectorAll('[data-native-product-id]'))
            .concat(state.pendingCells)
            .map((cell) => cell.getAttribute('data-native-product-id'))
            .filter(Boolean),
        );
        let lastFetchedUrl = state.pendingPageUrl;

        while (state.nextUrl && state.pendingCells.length < (state.batchSize || 4)) {
          const requestPage = state.needsBasePage ? 1 : null;
          const requestedPage = requestPage == null ? pageNumber(state.nextUrl) : requestPage;
          const requestPath = hrefForFetch(state.nextUrl, requestPage);
          const requestUrl = new URL(requestPath, window.location.href);
          const response = await window.fetch(requestPath, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal,
          });
          if (!root.isConnected || !grid.isConnected) return;
          if (!response || !response.ok || response.redirected) throw new Error('Collection feed request failed.');

          const finalUrl = resolveCollectionUrl(response.url || requestUrl.href);
          if (!finalUrl || pageNumber(finalUrl) !== requestedPage) throw new Error('Collection feed returned another URL.');

          const html = await response.text();
          if (!root.isConnected || !grid.isConnected) return;
          const parsed = new window.DOMParser().parseFromString(html, 'text/html');
          const feed = parsed.querySelector(feedSelector);
          const feedGrid = feed && feed.querySelector(gridSelector);
          if (!feed || !feedGrid) throw new Error('Collection feed markup was not found.');

          const nextLink = feed.querySelector(`${rootSelector} ${moreSelector}`);
          const nextUrl = nextLink && resolveCollectionUrl(nextLink.getAttribute('href'));
          if (nextLink && (!nextUrl || pageNumber(nextUrl) <= requestedPage)) throw new Error('Collection feed pagination did not advance.');

          Array.from(feedGrid.children)
            .filter((cell) => cell.hasAttribute('data-native-product-id'))
            .forEach((sourceCell) => {
              const productId = sourceCell.getAttribute('data-native-product-id');
              if (!productId || seenIds.has(productId)) return;
              seenIds.add(productId);
              state.pendingCells.push(document.importNode(sourceCell, true));
            });

          lastFetchedUrl = finalUrl;
          if (!state.pendingPageUrl && state.pendingCells.length) state.pendingPageUrl = finalUrl;
          state.needsBasePage = false;
          state.nextUrl = nextUrl;
          // A heavily overlapping page must not leave the customer with an empty batch.
        }

        // A fixed batch can span overlapping pages; retain every spare card.
        // Older markup keeps its existing complete-row batching.
        const batchCount = state.batchSize ? Math.min(state.batchSize, state.pendingCells.length) : state.nextUrl
          ? Math.floor(state.pendingCells.length / 4) * 4
          : state.pendingCells.length;
        appendedCells = state.pendingCells.splice(0, batchCount);
        state.pendingPageUrl = state.pendingCells.length ? lastFetchedUrl : null;
        const currentCell = Array.from(grid.children).find((child) => child.hasAttribute('data-native-product-id'));
        const layoutClassName = currentCell ? currentCell.className : '';
        const currentPhoto = currentCell && currentCell.querySelector('.ProductItem__Image[sizes]');
        const imageSizes = currentPhoto && currentPhoto.getAttribute('sizes');
        const fragment = document.createDocumentFragment();
        appendedCells.forEach((cell) => {
          if (layoutClassName) cell.className = layoutClassName;
          // The feed is shared by different grid layouts. Match the current grid
          // before insertion so the browser never requests an oversized first copy.
          if (imageSizes) cell.querySelectorAll('.ProductItem__Image, .model-image').forEach((image) => image.setAttribute('sizes', imageSizes));
          fragment.appendChild(cell);
        });
        grid.appendChild(fragment);

        if (state.nextUrl || state.pendingCells.length) {
          const fallbackUrl = state.pendingPageUrl || state.nextUrl;
          link.href = `${fallbackUrl.pathname}${fallbackUrl.search}`;
          status.textContent = appendedCells.length
            ? `Added ${appendedCells.length} ${appendedCells.length === 1 ? 'product' : 'products'}.`
            : 'No additional products on this page. Select Load more to continue.';

          if (keyboardActivation) focusWithoutScroll(firstProductLink(appendedCells[0]) || link);
        } else {
          state.nextUrl = null;
          link.remove();
          status.textContent = 'You have reached the end of the collection.';
          status.tabIndex = -1;
          if (keyboardActivation || document.activeElement === link) focusWithoutScroll(status);
        }
        state.autoPaused = false;
      } catch (_error) {
        // A failed automatic request waits for an explicit retry.
        state.autoPaused = true;
        if (root.isConnected && grid.isConnected) {
          status.setAttribute('data-native-pagination-error', '');
          status.textContent = 'Products could not be loaded. Try again.';
        }
      } finally {
        window.clearTimeout(timeout);
        state.loading = false;
        if (root.isConnected && link.isConnected && (state.nextUrl || state.pendingCells.length)) setLoading(root, link, status, false);
        else if (root.isConnected) root.removeAttribute('aria-busy');
        watchForMore();
      }
    }

    if (typeof window.IntersectionObserver === 'function' && typeof window.fetch === 'function' && typeof window.DOMParser === 'function') {
      try {
        observer = new window.IntersectionObserver((entries) => {
          if (!root.isConnected || !link.isConnected) {
            observer.disconnect();
            return;
          }
          if (!state.autoPaused && !state.suspended && !document.hidden && entries.some((entry) => entry.target === link && entry.isIntersecting)) {
            void loadProducts();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
        watchForMore();
        document.addEventListener('visibilitychange', watchForMore);
        window.addEventListener('pagehide', () => { state.suspended = true; if (observer) observer.disconnect(); });
        window.addEventListener('pageshow', () => { state.suspended = false; watchForMore(); });
      } catch (_error) {
        if (observer) observer.disconnect();
        observer = null;
      }
    }
  }

  function initializeAll() {
    document.querySelectorAll(rootSelector).forEach(initialize);
  }

  initializeAll();
})();
